<template>
  <div
    v-if="isLoadingFlowsUrl"
    class="h-full flex items-center justify-center"
  >
    <GlobalLoading :is-loading="true" />
  </div>

  <div
    v-else-if="showError"
    class="h-full flex items-center justify-center"
  >
    <div>
      <p class="text-destructive-emphasis text-base">
        {{ flowsErrorMessage || 'Failed to load Flows' }}
      </p>

      <button
        v-if="showErrorReloadButton"
        type="button"
        class="bg-transparent w-full flex items-center justify-center h-8 p-2 border-[0.5px] border-solid border-default hover:border-brand-600 focus:border-brand-600 focus:bg-brand-50 rounded hover:bg-brand-50"
        @click="reloadFlows"
      >
        Reload Flows
      </button>
    </div>
  </div>

  <div
    v-else-if="iframeUrl"
    class="flex h-full w-full flex-col"
  >
    <div class="bg-default">
      <header class="flex items-center justify-between px-6 py-4">
        <div class="flex items-center gap-3">
          <h1 class="text-2xl font-medium text-emphasis">
            Flows
          </h1>

          <FlowsSpaceToggle
            v-if="!sharedBuilder"
            :active-space="effectiveActiveSpace"
            :is-shared-disabled="!iframeUrl?.sharedSpace"
            :is-private-disabled="!iframeUrl?.privateSpace"
            @space-change="handleSpaceChange"
            @how-it-works-click="isModalOpen = true"
          />
        </div>

        <div class="flex items-center gap-4">
          <a
            :href="FLOWS_HELP_URL"
            target="_blank"
            rel="noopener noreferrer"
            class="flex items-center gap-1 rounded border border-solid border-default bg-white
            px-3 py-1.5 text-sm font-semibold text-emphasis"
          >
            Help with Flows
            <Icon
              type="link-external"
              class="h-3.5 w-3.5"
            />
          </a>
        </div>
      </header>

      <FlowsPrivateSpaceBanner v-if="hasBothSpaces && isPrivateSpace && !sharedBuilder" />

      <FlowsSharedOnlyBanner
        v-if="isSharedSpace && !sharedBuilder"
        :app-name="appName"
      />
    </div>

    <GlobalLoading :is-loading="isLoadingIframe" />

    <iframe
      class="min-h-0 w-full flex-1"
      :class="{ 'hidden': isLoadingIframe }"
      :src="activeIframeUrl"
      sandbox="allow-scripts allow-same-origin allow-storage-access-by-user-activation allow-modals allow-popups allow-forms"
      @load="onIframeLoad"
    />

    <SharedVsPrivateModal
      v-if="!sharedBuilder"
      :is-open="isModalOpen"
      @close="isModalOpen = false"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Api from '@/lib/api-wrapper';
import RequestUtils from '@/components/util/RequestUtils';
import Icon from '@/components/ui/Icon.vue';
import GlobalLoading from '@/components/ui/GlobalLoading.vue';
import { isSharedBuilder } from '@/lib/plan-helper';
import FlowsSpaceToggle from './FlowsSpaceToggle.vue';
import FlowsPrivateSpaceBanner from './FlowsPrivateSpaceBanner.vue';
import FlowsSharedOnlyBanner from './FlowsSharedOnlyBanner.vue';
import SharedVsPrivateModal from './SharedVsPrivateModal.vue';

const FLOWS_HELP_URL = 'https://docs.knack.com/v5/docs/flows-getting-started';

export default {
  name: 'FlowsList',
  components: {
    GlobalLoading,
    FlowsSpaceToggle,
    FlowsPrivateSpaceBanner,
    FlowsSharedOnlyBanner,
    SharedVsPrivateModal,
    Icon,
  },
  mixins: [
    RequestUtils,
  ],
  data() {
    return {
      iframeUrl: null,
      flowsErrorMessage: null,
      isErrorReloadButtonShown: false,
      isLoadingIframe: true,
      isLoadingFlowsUrl: true,
      activeSpace: 'shared',
      isModalOpen: false,
      FLOWS_HELP_URL,
    };
  },
  computed: {
    ...mapGetters(['appName']),
    sharedBuilder() {
      return isSharedBuilder();
    },
    hasBothSpaces() {
      return !!(this.iframeUrl?.privateSpace && this.iframeUrl?.sharedSpace);
    },
    effectiveActiveSpace() {
      return !this.iframeUrl?.sharedSpace && this.iframeUrl?.privateSpace
        ? 'private'
        : this.activeSpace;
    },
    showError() {
      if (this.flowsErrorMessage) return true;
      if (this.iframeUrl && this.sharedBuilder && !this.iframeUrl.sharedSpace) return true;
      return false;
    },
    showErrorReloadButton() {
      // For API errors, respect the errorCode check (e.g. flows_multiple_regions can't be fixed by reloading)
      if (this.flowsErrorMessage) return this.isErrorReloadButtonShown;
      // For shared builder with no sharedSpace, reloading may help
      return true;
    },
    activeIframeUrl() {
      if (this.sharedBuilder) return this.iframeUrl?.sharedSpace || '';
      return this.effectiveActiveSpace === 'shared'
        ? (this.iframeUrl?.sharedSpace || '')
        : (this.iframeUrl?.privateSpace || '');
    },
    isPrivateSpace() {
      return this.activeIframeUrl === this.iframeUrl?.privateSpace;
    },
    isSharedSpace() {
      return this.activeIframeUrl === this.iframeUrl?.sharedSpace;
    },
  },
  created() {
    this.loadFlows();
  },
  methods: {
    onIframeLoad() {
      this.isLoadingIframe = false;
    },
    reloadFlows() {
      this.resetFlowsUrl();
      this.isLoadingFlowsUrl = true;
      this.flowsErrorMessage = null;
      this.isLoadingIframe = true;
      this.loadFlows();
    },
    handleSpaceChange(space) {
      if (space === this.effectiveActiveSpace) return;
      this.activeSpace = space;
      this.isLoadingIframe = true;
    },
    loadFlows() {
      const cached = this.getFlowsUrl();
      if (cached) {
        this.iframeUrl = cached;
        this.isLoadingFlowsUrl = false;
        return;
      }

      this.iframeUrl = null;
      this.flowsErrorMessage = null;

      this.commitRequest({
        request: () => {
          const options = {
            url: '/flows/get-embedded-ui-url',
            method: 'POST',
            encoding: null,
          };
          return Api.axios(options);
        },
        onSuccess: (result) => {
          this.iframeUrl = result.iframeUrl;
          this.setFlowsUrl(result.iframeUrl);
          this.isLoadingFlowsUrl = false;
        },
        onError: (result) => {
          this.flowsErrorMessage = result?.[0]?.error || 'Failed to load Flows';
          // don't show the reload button for the multiple regions error because reloading won't help
          this.isErrorReloadButtonShown = result?.[0]?.errorCode !== 'flows_multiple_regions';
          this.isLoadingFlowsUrl = false;
        },
      });
    },
    getFlowsUrl() {
      return this.$store.getters.getFlowsUrl();
    },
    setFlowsUrl(iframeUrl) {
      this.$store.commit('setCacheFlowsUrl', iframeUrl);
    },
    resetFlowsUrl() {
      this.$store.commit('setCacheFlowsUrl', null);
    },
  },
};
</script>
